import React, { useState } from 'react';
import { THEMES, THEME_ORDER, THEME_VARIATIONS, DENSITY_MODES, applyVariation } from '../../themes/themes';
import { CARD_STYLES } from '../../themes/cardStyles';

// Matches the options available in App.js's ThemeConfiguratorPage (theme,
// variation, density, card style, hover effects) and adds the dashboard-
// specific Campaign Page Style (Ornate vs Modern). State changes are
// persisted via the apply callback to localStorage in PlayerDashboard,
// keeping the dashboard's theme in sync with App.js's existing theme.
//
// `scopeContext` shapes the scope picker at the top of the modal:
//   - 'global'   -> only "Everything" / "Dashboard only" are offered.
//   - { kind:'campaign', campaignId, campaignName }  -> all three options
//                including "This campaign only", which is the default
//                when opened from a campaign detail view.
export const ThemePickerModal = ({
  theme,
  themeId,
  variationId,
  density,
  cardStyle,
  hoverEffects,
  campaignStyle,
  scopeContext = 'global',
  onApply,
  onClose,
}) => {
  const c = theme.colors;
  const t = theme.typography || {};
  const e = theme.effects || {};
  const isCampaignScopeAvailable = scopeContext && scopeContext.kind === 'campaign' && !!scopeContext.campaignId;
  const [scope, setScope] = useState(() => (isCampaignScopeAvailable ? 'campaign' : 'everything'));
  const [selectedTheme, setSelectedTheme] = useState(themeId);
  const [selectedVariation, setSelectedVariation] = useState(variationId);
  const [selectedDensity, setSelectedDensity] = useState(density || 'comfortable');
  const [selectedCard, setSelectedCard] = useState(cardStyle);
  const [enableHoverEffects, setEnableHoverEffects] = useState(hoverEffects !== false);
  const [selectedCampaignStyle, setSelectedCampaignStyle] = useState(campaignStyle || 'ornate');

  const previewTheme = applyVariation(THEMES[selectedTheme] || THEMES['fantasy-classic'], selectedVariation);
  const variations = THEME_VARIATIONS[selectedTheme] || [{ id: 'default', name: 'Default' }];
  const themesList = THEME_ORDER.map(id => THEMES[id]).filter(Boolean);

  return (
    <div style={{
      position: 'fixed', inset: 0,
      background: c.bgModal || 'rgba(0,0,0,0.85)',
      backdropFilter: 'blur(6px)',
      zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 20,
    }} onClick={onClose}>
      <div
        onClick={ev => ev.stopPropagation()}
        style={{
          background: c.bgCard,
          border: e.cardBorder || `1px solid ${c.borderPrimary}`,
          borderRadius: e.borderRadiusLg || '12px',
          padding: 24,
          maxWidth: 820,
          width: '100%',
          maxHeight: '85vh',
          overflow: 'auto',
          boxShadow: e.cardShadow,
          fontFamily: t.fontFamily,
          color: c.textPrimary,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{
            margin: 0,
            color: c.accentLight,
            fontFamily: t.headingFamily,
            fontSize: '1.3rem',
            textShadow: e.headingTextShadow,
          }}>🎨 Choose Theme</h2>
          <button onClick={onClose} style={{
            background: 'transparent',
            border: `1px solid ${c.borderPrimary}`,
            color: c.textSecondary,
            borderRadius: e.borderRadiusSm || '4px',
            padding: '6px 12px',
            cursor: 'pointer',
            fontFamily: t.fontFamily,
          }}>✕ Close</button>
        </div>

        {/* Scope picker — where does this theme apply?
            'everything'    -> global localStorage keys, shared with App.js
            'dashboard'     -> dashboard-only override keys
            'campaign'      -> per-campaign override keys (only available
                               when the modal was opened from a campaign
                               page; campaign name shown for clarity) */}
        <div style={{ marginBottom: 18 }}>
          <div style={{
            fontSize: '0.72rem',
            color: c.textMuted,
            textTransform: 'uppercase',
            letterSpacing: '0.18em',
            marginBottom: 8,
            fontWeight: 600,
          }}>Apply to</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {[
              { id: 'everything', label: 'Everything',     desc: 'all surfaces, persisted globally' },
              { id: 'dashboard',  label: 'Dashboard only', desc: 'leaves App theme untouched' },
              ...(isCampaignScopeAvailable
                ? [{
                    id: 'campaign',
                    label: scopeContext.campaignName ? `${scopeContext.campaignName} only` : 'This campaign only',
                    desc: 'applies on this campaign\'s pages',
                  }]
                : []),
            ].map((opt) => {
              const active = opt.id === scope;
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setScope(opt.id)}
                  title={opt.desc}
                  style={{
                    padding: '6px 12px',
                    background: active ? c.bgHover : 'transparent',
                    border: `1px solid ${active ? c.accent : c.borderPrimary}`,
                    borderRadius: e.borderRadiusSm || '4px',
                    color: active ? c.accent : c.textSecondary,
                    fontSize: '0.78rem',
                    cursor: 'pointer',
                    fontFamily: t.fontFamily,
                  }}
                >{opt.label}</button>
              );
            })}
          </div>
        </div>

        {/* Card style picker */}
        <div style={{ marginBottom: 18 }}>
          <div style={{
            fontSize: '0.72rem',
            color: c.textMuted,
            textTransform: 'uppercase',
            letterSpacing: '0.18em',
            marginBottom: 8,
            fontWeight: 600,
          }}>Card Style</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {Object.values(CARD_STYLES).map(cs => {
              const active = cs.id === selectedCard;
              return (
                <button key={cs.id} onClick={() => setSelectedCard(cs.id)} style={{
                  padding: '6px 12px',
                  background: active ? c.bgHover : 'transparent',
                  border: `1px solid ${active ? c.accent : c.borderPrimary}`,
                  borderRadius: e.borderRadiusSm || '4px',
                  color: active ? c.accent : c.textSecondary,
                  fontSize: '0.78rem',
                  cursor: 'pointer',
                  fontFamily: t.fontFamily,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                }}>{cs.icon} {cs.name}</button>
              );
            })}
          </div>
        </div>

        {/* Density picker — mirrors the App's character-sheet theme page. */}
        <div style={{ marginBottom: 18 }}>
          <div style={{
            fontSize: '0.72rem',
            color: c.textMuted,
            textTransform: 'uppercase',
            letterSpacing: '0.18em',
            marginBottom: 8,
            fontWeight: 600,
          }}>Density</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {Object.values(DENSITY_MODES).map(d => {
              const active = d.id === selectedDensity;
              return (
                <button
                  key={d.id}
                  onClick={() => setSelectedDensity(d.id)}
                  title={d.description}
                  style={{
                    padding: '6px 12px',
                    background: active ? c.bgHover : 'transparent',
                    border: `1px solid ${active ? c.accent : c.borderPrimary}`,
                    borderRadius: e.borderRadiusSm || '4px',
                    color: active ? c.accent : c.textSecondary,
                    fontSize: '0.78rem',
                    cursor: 'pointer',
                    fontFamily: t.fontFamily,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >{d.icon} {d.name}</button>
              );
            })}
          </div>
        </div>

        {/* Hover effects toggle */}
        <div style={{ marginBottom: 18 }}>
          <label style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 10,
            cursor: 'pointer',
            color: c.textSecondary,
            fontFamily: t.fontFamily,
            fontSize: '0.84rem',
          }}>
            <input
              type="checkbox"
              checked={enableHoverEffects}
              onChange={(ev) => setEnableHoverEffects(ev.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            <span>Enable hover effects (lift / glow / transitions)</span>
          </label>
        </div>

        {/* Campaign Page Style — bigger user-facing decision so it gets
            two tile-style options rather than pill toggles. */}
        <div style={{ marginBottom: 18 }}>
          <div style={{
            fontSize: '0.72rem',
            color: c.textMuted,
            textTransform: 'uppercase',
            letterSpacing: '0.18em',
            marginBottom: 8,
            fontWeight: 600,
          }}>Campaign Page Style</div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 10,
          }}>
            {[
              {
                id: 'ornate',
                name: 'Ornate',
                icon: '✦',
              },
              {
                id: 'modern',
                name: 'Modern',
                icon: '◇',
              },
            ].map(opt => {
              const active = opt.id === selectedCampaignStyle;
              return (
                <button
                  key={opt.id}
                  onClick={() => setSelectedCampaignStyle(opt.id)}
                  style={{
                    padding: '8px 12px',
                    background: active ? c.bgHover : 'rgba(0,0,0,0.18)',
                    border: `1px solid ${active ? c.accent : c.borderPrimary}`,
                    borderRadius: e.borderRadiusSm || '6px',
                    cursor: 'pointer',
                    fontFamily: t.fontFamily,
                    textAlign: 'left',
                    transition: 'all 150ms ease',
                    boxShadow: active ? `0 0 12px ${c.accentGlow || `${c.accent}55`}` : 'none',
                    display: 'flex', alignItems: 'center', gap: 8,
                  }}
                >
                  <span style={{
                    fontSize: '1rem',
                    color: active ? c.accentLight : c.textSecondary,
                  }}>{opt.icon}</span>
                  <span style={{
                    fontSize: '0.92rem',
                    fontWeight: 700,
                    color: active ? c.accentLight : c.textPrimary,
                    letterSpacing: '0.02em',
                  }}>{opt.name}</span>
                </button>
              );
            })}
          </div>
        </div>

        {variations.length > 1 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{
              fontSize: '0.72rem',
              color: c.textMuted,
              textTransform: 'uppercase',
              letterSpacing: '0.18em',
              marginBottom: 8,
              fontWeight: 600,
            }}>Variation</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {variations.map(v => {
                const active = v.id === selectedVariation;
                return (
                  <button key={v.id} onClick={() => setSelectedVariation(v.id)} style={{
                    padding: '6px 12px',
                    background: active ? c.bgHover : 'transparent',
                    border: `1px solid ${active ? c.accent : c.borderPrimary}`,
                    borderRadius: e.borderRadiusSm || '4px',
                    color: active ? c.accent : c.textSecondary,
                    fontSize: '0.78rem',
                    cursor: 'pointer',
                    fontFamily: t.fontFamily,
                  }}>{v.name}</button>
                );
              })}
            </div>
          </div>
        )}

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
          gap: 8,
          marginBottom: 20,
        }}>
          {themesList.map(th => {
            const active = th.id === selectedTheme;
            return (
              <div
                key={th.id}
                onClick={() => { setSelectedTheme(th.id); setSelectedVariation('default'); }}
                style={{
                  padding: 12,
                  background: active ? c.bgHover : c.bgInput,
                  border: `2px solid ${active ? c.accent : c.borderLight}`,
                  borderRadius: e.borderRadiusSm || '6px',
                  cursor: 'pointer',
                  textAlign: 'center',
                  transition: e.transition,
                  fontFamily: t.fontFamily,
                }}
              >
                <div style={{ fontSize: '1.5rem', marginBottom: 4 }}>{th.preview}</div>
                <div style={{
                  fontSize: '0.72rem',
                  color: active ? c.accent : c.textSecondary,
                  fontWeight: active ? 700 : 400,
                }}>{th.name}</div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: 3,
                  marginTop: 6,
                }}>
                  {[th.colors.accent, th.colors.success, th.colors.danger, th.colors.info, th.colors.warning].map((col, i) => (
                    <div key={i} style={{
                      width: 8, height: 8, borderRadius: '50%',
                      background: col,
                      border: '1px solid rgba(0,0,0,0.4)',
                    }} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{
            padding: '9px 18px',
            background: 'transparent',
            border: `1px solid ${c.borderPrimary}`,
            color: c.textSecondary,
            borderRadius: e.borderRadiusSm || '4px',
            cursor: 'pointer',
            fontFamily: t.fontFamily,
          }}>Cancel</button>
          <button
            onClick={() => {
              onApply({
                scope,
                campaignId: scope === 'campaign' && isCampaignScopeAvailable
                  ? scopeContext.campaignId
                  : null,
                themeId: selectedTheme,
                variationId: selectedVariation,
                density: selectedDensity,
                cardStyle: selectedCard,
                hoverEffects: enableHoverEffects,
                campaignStyle: selectedCampaignStyle,
              });
              onClose();
            }}
            style={{
              padding: '9px 22px',
              background: `linear-gradient(180deg, ${c.accent} 0%, ${c.accentDark} 100%)`,
              border: `1px solid ${c.accentLight}`,
              color: c.textOnAccent,
              fontWeight: 700,
              borderRadius: e.borderRadiusSm || '4px',
              cursor: 'pointer',
              fontFamily: t.fontFamily,
              boxShadow: e.glowAccent,
            }}
          >✓ Apply</button>
        </div>
      </div>
    </div>
  );
};
